import React, { useEffect, useMemo, useState } from 'react';
import { Button, ToastMessage } from '../../components/ui';
import { UnifiedTask } from '../../api/unifiedTasks';
import { TaskContent } from '../../api/laneApi';
import {
  fetchFeeDecisionOptions,
  recordFeeWaiver,
  allowedReasonsFromError,
  errorMessageFrom,
  statusFrom,
  recordCallAttempt,
  FeeDecisionOptions,
  FeeWaiverResponse,
  CallAttempt,
} from '../../api/cancellationRingfence';
import { createManualCancellationPayment } from '../../api/payments';
import { HoldReviewSection } from '../../components/HoldReviewSection';
import './lanes.css';

/**
 * The 70+ cancellation ring-fence outcome panel, rendered inside
 * `TaskDetailPane` for a `cancellation_ringfenced` task and nothing else.
 *
 * Background: a client aged 70+ (or one whose date of birth cannot be read at
 * all -- fail safe) who cancels still has their claim cancelled, but the
 * charge gate suppresses the entire Stripe chain. Instead of an invoice they
 * get a task in the manual lane, and a member of staff phones them. This
 * panel is where the outcome of that call is recorded: waive the fee, or
 * charge it anyway.
 *
 * Why the facts are re-rendered here rather than left to the pane's generic
 * sections: the ring-fence task is created with `type='email_task'` and
 * `emailType='manual'`, which `get_lane_task` collapses to `type: 'manual'`.
 * TaskDetailPane's manual branch shows `task.description` -- a column
 * `create_email_task` never populates -- and its email branch is switched off
 * for a manual type, so the task's narrative body would otherwise render
 * nowhere at all. The structured fields come from
 * `claudeAnalysis.extractedData` (Task 2's deliberate machine-readable half),
 * never from parsing that body.
 */

/** `claudeAnalysis.extractedData` as the ring-fence task writes it. Every
 * field is optional here because this pane must survive a half-written or
 * older analysis blob rather than blanking the whole panel. */
interface RingfenceExtractedData {
  claimIds?: string[];
  claimRefs?: string[];
  ringfenceReasons?: string[];
  age?: number | null;
  ageSource?: string | null;
  wouldHaveChargedClaimIds?: string[];
  clientIdentified?: boolean;
  /** Pre-cancellation hold (2026-08-18): present and equal to
   * 'pre_cancellation' only on a hold task -- switches this panel from the
   * shipped charge/waive UI to the review-call script. Absent on the legacy
   * post-cancel ring-fence task this panel already rendered. */
  holdType?: string;
  requestedReason?: string;
  requestedSource?: string;
}

export interface CancellationRingfencePanelProps {
  task: UnifiedTask;
  /** Already fetched by the pane -- not re-requested here. */
  content: TaskContent | null;
  showToast?: (toast: ToastMessage) => void;
  /** Called after the task is completed so the owning lane refetches. */
  onChanged?: () => void;
  /** Injected by the pane so the two "and complete the task" paths go through
   * the pane's own completion handling (status update + toast + onChanged)
   * rather than this panel keeping a second, divergent copy of it. */
  onComplete: (notes: string) => Promise<boolean>;
  /** False once the task is completed/archived -- the record is then history,
   * not a decision still to be made. */
  canAct: boolean;
}

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const asCallAttempts = (value: unknown): CallAttempt[] =>
  Array.isArray(value)
    ? value.filter(
        (v): v is CallAttempt =>
          !!v && typeof (v as CallAttempt).by === 'string' && typeof (v as CallAttempt).at === 'string'
      )
    : [];

const formatPounds = (amount: number): string =>
  `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const CancellationRingfencePanel: React.FC<CancellationRingfencePanelProps> = ({
  task, content, showToast, onChanged, onComplete, canAct,
}) => {
  const extracted = (content?.claudeAnalysis?.extractedData || {}) as RingfenceExtractedData;

  const claimIds = asStringArray(extracted.claimIds);
  const claimRefs = asStringArray(extracted.claimRefs);
  const chargeableClaimIds = asStringArray(extracted.wouldHaveChargedClaimIds);
  const ringfenceReasons = asStringArray(extracted.ringfenceReasons);
  const clientIdentified = extracted.clientIdentified !== false;
  const age = typeof extracted.age === 'number' ? extracted.age : null;
  const ageSource = typeof extracted.ageSource === 'string' ? extracted.ageSource : null;

  const initialCallAttempts = asCallAttempts(
    (content?.claudeAnalysis as Record<string, any> | null)?.callAttempts
  );

  // The decision is recorded against one claim and the server fans it out
  // across the whole cancelled group. `assignedClaimId` is the claim the task
  // was raised against (the first chargeable one, where there is one), so it
  // is the right anchor; the extracted list is the fallback for a task whose
  // assignment was later changed by hand.
  const decisionClaimId = task.assignedClaimId || claimIds[0] || null;

  const body = (content?.emailContent as Record<string, any> | null)?.body as string | undefined;

  const [options, setOptions] = useState<FeeDecisionOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [waiveOpen, setWaiveOpen] = useState(false);
  const [chargeOpen, setChargeOpen] = useState(false);

  /** Set when a waiver saved but part of it did not land. Sticky: it stays on
   * screen until the task is reloaded, because nothing else in the UI will
   * tell anyone that one of this client's claims still looks chargeable. */
  const [waiverWarnings, setWaiverWarnings] = useState<string[]>([]);
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    // Modal/result state is task-scoped. TaskDetailPane stays mounted as the
    // operator moves down the lane, so explicitly discard anything belonging
    // to the previous client before the next task can be acted on.
    setWaiveOpen(false);
    setChargeOpen(false);
    setOptions(null);
    setWaiverWarnings([]);
    setOutcome(null);
  }, [task.id]);

  useEffect(() => {
    let cancelled = false;
    setOptionsLoading(true);
    setOptionsError(null);
    fetchFeeDecisionOptions(chargeableClaimIds.length)
      .then((data) => {
        if (cancelled) return;
        // A payload without a reason list is a failure, not an empty
        // dropdown: the write path 400s any code outside the server's
        // vocabulary, so an empty select would let a staff member open the
        // dialog and find nothing they can record their call as.
        if (!data || !Array.isArray(data.reasons) || data.reasons.length === 0) {
          setOptionsError('Could not load the fee decision options.');
          return;
        }
        setOptions(data);
      })
      .catch(() => {
        if (!cancelled) setOptionsError('Could not load the fee decision options.');
      })
      .finally(() => { if (!cancelled) setOptionsLoading(false); });
    return () => { cancelled = true; };
    // Re-run per task, not per render -- the chargeable count is a property of
    // the task, and the reason vocabulary never changes within a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id, chargeableClaimIds.length]);

  const fee = options?.fee || null;

  const feeText = useMemo(() => {
    if (chargeableClaimIds.length === 0) {
      return 'Nothing — no claim in this cancellation met the charging conditions. Courtesy review only.';
    }
    if (optionsLoading) return 'Pricing…';
    if (!fee) {
      return `${chargeableClaimIds.length} chargeable claim(s) — amount unavailable`;
    }
    return `${formatPounds(fee.totalAmountPounds)} inc VAT `
      + `(${formatPounds(fee.baseAmountPounds)} + ${formatPounds(fee.vatAmountPounds)} VAT, `
      + `${fee.chargeableClaims} chargeable claim(s))`;
  }, [chargeableClaimIds.length, optionsLoading, fee]);

  // Pre-cancellation hold (2026-08-18): a hold task gets the review-call
  // script, not the shipped charge/waive panel. Legacy post-cancel tasks
  // (no holdType) fall through to the UI below, unchanged. This is a plain
  // derived value, not a hook, so it is safe to compute after the hooks
  // above without disturbing React's rules-of-hooks ordering.
  const isHoldTask = extracted.holdType === 'pre_cancellation';

  const handleWaived = async (response: FeeWaiverResponse) => {
    setWaiveOpen(false);
    const warnings = asStringArray(response.warnings);
    setWaiverWarnings(warnings);

    if (warnings.length > 0) {
      // Deliberately NOT completed. The response is a 200 and the decision is
      // on the claim in the URL, but something else in the write was lost --
      // a sibling claim of the same cancellation, the group lookup, or the
      // timeline memo. Completing here would drop the task out of the queue
      // while one of this client's claims still looks chargeable to every
      // downstream reader, which is precisely what `warnings` exists to stop.
      setOutcome(null);
      showToast?.({
        type: 'error',
        message: `Waiver recorded, but ${warnings.length} part(s) did not save — task left open.`,
      });
      onChanged?.();
      return;
    }

    setOutcome(
      `Fee waived on ${response.claimIds.length} claim(s) by ${response.waivedBy}.`
    );
    await onComplete(
      `Cancellation fee waived (${response.reason}) on ${response.claimIds.length} claim(s).`
    );
  };

  const handleCharged = async (summary: string) => {
    setChargeOpen(false);
    setWaiverWarnings([]);
    setOutcome(summary);
    await onComplete(summary);
  };

  return (
    <div className="task-detail__section ringfence">
      <h4 className="ringfence__title">70+ cancellation ring-fence</h4>
      {isHoldTask ? (
        <p className="ringfence__banner">
          Cancellation request <strong>HELD</strong> — nothing has been cancelled and the
          client has not been emailed. Phone them, work through the review script below,
          then record the outcome.
        </p>
      ) : (
        <p className="ringfence__banner">
          This client was <strong>not</strong> auto-invoiced. Phone them to confirm the
          cancellation, then record the outcome below.
        </p>
      )}

      <div className="ringfence__facts">
        <div className="task-detail__row">
          <strong>Client:</strong> {task.clientName || 'Unknown client'}
          {task.clientEmail ? ` · ${task.clientEmail}` : ''}
        </div>
        <div className="task-detail__row">
          <strong>Age at cancellation:</strong>{' '}
          {age !== null
            ? `${age}${ageSource ? ` (from ${ageSource})` : ''}`
            : 'Unknown — no usable date of birth on the claim'}
        </div>
        <div className="task-detail__row">
          <strong>Ring-fence reason:</strong> {ringfenceReasons.join(', ') || 'age-70-plus'}
        </div>
        <div className="task-detail__row">
          <strong>Claims {isHoldTask ? 'held' : 'cancelled'} ({claimRefs.length || claimIds.length}):</strong>{' '}
          {claimRefs.join(', ') || claimIds.join(', ') || 'unknown'}
        </div>
        {!isHoldTask && (
          <div className="task-detail__row ringfence__fee">
            <strong>Fee that would have been charged:</strong> {feeText}
          </div>
        )}
        {!isHoldTask && !clientIdentified && (
          <div className="ringfence__caution">
            The client could not be identified from this cancellation (no client ID, no
            user ID and no email on the claim), so this task covers only the claim above.
            Check for other claims held by the same person before calling.
          </div>
        )}
      </div>

      <CallAttemptsSection
        key={task.id}
        taskId={task.id}
        initialAttempts={initialCallAttempts}
        canAct={canAct}
        showToast={showToast}
      />

      {isHoldTask ? (
        <>
          {/* Whole-branch review, finding 4: the fee-decision options fetch
              (above) also feeds the hold script's waive dropdown (`reasons`
              below) -- when it fails, that dropdown just renders empty with
              no explanation for why Cancel+waive is effectively locked. This
              error was already tracked, just never rendered on this branch. */}
          {optionsError && <div className="ringfence__error">{optionsError}</div>}
          <HoldReviewSection
            key={task.id}
            taskId={task.id}
            canAct={canAct}
            reasons={options?.reasons || []}
            requestedReason={typeof extracted.requestedReason === 'string' ? extracted.requestedReason : null}
            clientEmail={task.clientEmail || null}
            showToast={showToast}
            onChanged={onChanged}
          />
        </>
      ) : (
        <>
          {optionsError && <div className="ringfence__error">{optionsError}</div>}

          {waiverWarnings.length > 0 && (
            <div className="ringfence__warnings" role="alert">
              <strong>The waiver saved, but part of it did not:</strong>
              <ul>
                {waiverWarnings.map((warning, i) => <li key={i}>{warning}</li>)}
              </ul>
              <span className="ringfence__warnings-action">
                This task has been left open on purpose. Check the claims listed above and
                record the decision again on anything still showing as chargeable.
              </span>
            </div>
          )}

          {outcome && <div className="ringfence__outcome" role="status">{outcome}</div>}

          {canAct && (
            <div className="ringfence__actions">
              <Button
                variant="primary"
                size="sm"
                disabled={!decisionClaimId || optionsLoading || !options}
                onClick={() => setWaiveOpen(true)}
              >
                Waive fee
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={chargeableClaimIds.length === 0}
                title={chargeableClaimIds.length === 0
                  ? 'No claim in this cancellation met the charging conditions, so there is no suppressed fee to raise.'
                  : undefined}
                onClick={() => setChargeOpen(true)}
              >
                Charge anyway
              </Button>
            </div>
          )}

          {body && (
            <details className="ringfence__body">
              <summary>Full task detail</summary>
              <pre className="ringfence__body-text">{body}</pre>
            </details>
          )}

          {waiveOpen && decisionClaimId && options && (
            <WaiveFeeModal
              claimId={decisionClaimId}
              claimCount={claimIds.length || 1}
              reasons={options.reasons}
              onWaived={handleWaived}
              onClose={() => setWaiveOpen(false)}
            />
          )}

          {chargeOpen && (
            <ChargeAnywayModal
              claimIds={chargeableClaimIds}
              claimRefs={claimRefs}
              fee={fee}
              clientName={task.clientName || null}
              onCharged={handleCharged}
              onClose={() => setChargeOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

/* ---------------------------------------------------------- call attempts */

interface CallAttemptsSectionProps {
  taskId: string;
  /** Seed history from the pane's content fetch (`claudeAnalysis.callAttempts`). */
  initialAttempts: CallAttempt[];
  canAct: boolean;
  showToast?: (toast: ToastMessage) => void;
}

const formatAttemptTime = (at: string): string => {
  const parsed = new Date(at);
  return Number.isNaN(parsed.getTime())
    ? at
    : parsed.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Amendment 2: "Attempted call — no answer" + the who/when history, on BOTH
 * task generations (the parent mounts this outside the hold/legacy fork).
 * Staff judge non-contactability from the count themselves -- no automatic
 * threshold or action. Mounted with key={task.id} so state resets per task;
 * once the server returns a list it supersedes the seed, so a late-arriving
 * content prop can never mask a just-recorded attempt.
 */
export const CallAttemptsSection: React.FC<CallAttemptsSectionProps> = ({
  taskId, initialAttempts, canAct, showToast,
}) => {
  const [recorded, setRecorded] = useState<CallAttempt[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const attempts = recorded ?? initialAttempts;

  const record = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await recordCallAttempt(taskId);
      setRecorded(updated);
      showToast?.({
        type: 'success',
        message: `Call attempt recorded (${updated.length} so far).`,
      });
    } catch (err) {
      // The attempt was NOT saved; the task stays fully actionable.
      setError(errorMessageFrom(err, 'Failed to record the call attempt — nothing was saved.'));
    }
    setSaving(false);
  };

  return (
    <div className="ringfence__call-attempts">
      <div className="task-detail__row">
        <strong>Call attempts ({attempts.length}):</strong>{' '}
        {attempts.length === 0 ? 'none recorded yet' : ''}
      </div>
      {attempts.length > 0 && (
        <ul className="ringfence__call-attempts-list">
          {attempts.map((attempt, index) => (
            <li key={`${attempt.at}-${index}`}>
              {attempt.by || 'unknown'} — {formatAttemptTime(attempt.at)}
            </li>
          ))}
        </ul>
      )}
      {error && <div className="ringfence__error" role="alert">{error}</div>}
      {canAct && (
        <Button variant="secondary" size="sm" disabled={saving} onClick={record}>
          {saving ? 'Recording…' : 'Attempted call — no answer'}
        </Button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ waive */

interface WaiveFeeModalProps {
  claimId: string;
  claimCount: number;
  reasons: import('../../api/cancellationRingfence').WaiverReasonOption[];
  onWaived: (response: FeeWaiverResponse) => void;
  onClose: () => void;
}

export const WaiveFeeModal: React.FC<WaiveFeeModalProps> = ({
  claimId, claimCount, reasons, onWaived, onClose,
}) => {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = reasons.find((r) => r.code === reason) || null;
  const trimmedNote = note.trim();
  const noteMissing = !!selected?.noteRequired && !trimmedNote;
  const canSave = !!selected && !noteMissing && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const response = await recordFeeWaiver(claimId, {
        waived: true,
        reason,
        note: trimmedNote,
      });
      onWaived(response);
    } catch (err) {
      const allowed = allowedReasonsFromError(err);
      setError(
        allowed
          ? `${errorMessageFrom(err, 'The waiver was rejected.')} Accepted codes: ${allowed.join(', ')}.`
          : errorMessageFrom(err, 'Failed to record the waiver.')
      );
      setSaving(false);
    }
  };

  return (
    <div className="lane-modal-backdrop" onClick={onClose}>
      <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">Waive the cancellation fee</h2>
        <p className="lane-modal__hint">
          Recorded against claim {claimId} and every claim cancelled with it
          {claimCount > 1 ? ` (${claimCount} in this cancellation)` : ''}.
        </p>

        {error && <div className="lane-modal__error" role="alert">{error}</div>}

        <div className="lane-modal__field">
          <label htmlFor="ringfence-waiver-reason">Reason</label>
          <select
            id="ringfence-waiver-reason"
            className="lane-modal__input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          >
            <option value="">Select a reason…</option>
            {reasons.map((option) => (
              <option key={option.code} value={option.code}>{option.label}</option>
            ))}
          </select>
        </div>

        <div className="lane-modal__field">
          <label htmlFor="ringfence-waiver-note">
            Note{selected?.noteRequired ? ' (required)' : ' (optional)'}
          </label>
          <textarea
            id="ringfence-waiver-note"
            className="lane-modal__input"
            rows={4}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What was agreed on the call?"
          />
          {noteMissing && (
            <div className="lane-modal__hint">A note is required with this reason.</div>
          )}
        </div>

        {/* Task 3, concern 2: recording a waiver writes the decision onto the
            claims. It does NOT void a Stripe link or touch cancellationPayments,
            so "waived" here and "pending" on a payment can coexist. In the
            normal ring-fence flow no payment exists (that is the whole point of
            the gate), but staff who charged first and then changed their mind
            must not read this as the invoice having been pulled. */}
        <p className="lane-modal__hint">
          This records the decision on the claim. It does not cancel a payment link
          that has already been sent — void that from Cancellation Payments.
        </p>

        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          {/* Not "Waive fee" -- that is the panel button that opened this
              dialog, and two controls with the same accessible name in the
              same document is ambiguous to a screen reader (and to a test). */}
          <Button variant="primary" onClick={handleSave} disabled={!canSave}>
            {saving ? 'Recording…' : 'Record waiver'}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------- charge */

interface ChargeAnywayModalProps {
  claimIds: string[];
  claimRefs: string[];
  fee: import('../../api/cancellationRingfence').RingfenceFee | null;
  clientName: string | null;
  onCharged: (summary: string) => void;
  onClose: () => void;
}

export const ChargeAnywayModal: React.FC<ChargeAnywayModalProps> = ({
  claimIds, claimRefs, fee, clientName, onCharged, onClose,
}) => {
  // Pre-filled with the EX-VAT figure the server priced. The manual payment
  // route treats `amount` as the figure before VAT and adds VAT itself, while
  // the task body quotes the inc-VAT total -- typing the number from the body
  // in here would bill the client VAT on VAT.
  const [amount, setAmount] = useState(fee ? fee.baseAmountPounds.toFixed(2) : '');
  const [description, setDescription] = useState(
    `Cancellation fee agreed by phone (70+ ring-fence review)${clientName ? ` — ${clientName}` : ''}`
  );
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = Number(amount);
  const amountValid = Number.isFinite(parsed) && parsed > 0;

  // Derived from the server's own figures rather than a hard-coded 20%, so a
  // configured VAT rate (Config.STRIPE_VAT_RATE) is reflected here too.
  const vatRate = fee && fee.baseAmountPounds > 0
    ? fee.vatAmountPounds / fee.baseAmountPounds
    : null;
  const preview = amountValid && vatRate !== null
    ? `${formatPounds(parsed)} + ${formatPounds(parsed * vatRate)} VAT = `
      + `${formatPounds(parsed * (1 + vatRate))} to pay`
    : null;

  const handleCreate = async () => {
    if (!amountValid || creating) return;
    setCreating(true);
    setError(null);
    try {
      const response = await createManualCancellationPayment({
        claimIds,
        amount: parsed,
        description: description.trim(),
      });
      onCharged(
        `Payment link raised for ${formatPounds(response.amount)} `
        + `on ${claimIds.length} claim(s).`
      );
    } catch (err) {
      const status = statusFrom(err);
      setError(
        status === 403
          ? 'You do not have permission to raise a payment. Raising a cancellation '
            + 'payment needs the admin or payments-manager role — ask one of them to '
            + 'charge this client, or waive the fee instead.'
          : errorMessageFrom(err, 'Failed to raise the payment link.')
      );
      setCreating(false);
    }
  };

  return (
    <div className="lane-modal-backdrop" onClick={onClose}>
      <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">Charge the cancellation fee anyway</h2>
        <p className="lane-modal__hint">
          Raises a Stripe payment link and emails it to the client for{' '}
          {claimRefs.join(', ') || claimIds.join(', ')}.
        </p>

        {error && <div className="lane-modal__error" role="alert">{error}</div>}

        <div className="lane-modal__field">
          <label htmlFor="ringfence-charge-amount">Amount excluding VAT (£)</label>
          <input
            id="ringfence-charge-amount"
            className="lane-modal__input"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
          <div className="lane-modal__hint">
            {preview || 'VAT is added to this figure automatically.'}
          </div>
          {!fee && (
            <div className="lane-modal__hint">
              The suppressed fee could not be priced — enter the amount before VAT.
            </div>
          )}
        </div>

        <div className="lane-modal__field">
          <label htmlFor="ringfence-charge-description">Description</label>
          <input
            id="ringfence-charge-description"
            className="lane-modal__input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={onClose} disabled={creating}>Cancel</Button>
          <Button variant="danger" onClick={handleCreate} disabled={!amountValid || creating}>
            {creating ? 'Raising…' : 'Raise payment link'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CancellationRingfencePanel;
